/* =========================================================================
 * THE SPLIT PLAN — one record that holds many championships, made honest.
 *
 * Brian: "if the timing is right — parent folder with children. But if it's
 * like one now, one in the spring, they are split."
 * Brian: "it becomes the new factual record."
 * Brian: "a championship date is extremely important. 21 days might as well be
 * an eternity."
 * Brian: "we need to make sure this includes the summary. Rep should be able to
 * review this and do any inputs to either design a ring or step into a meeting
 * with the director of the team or coach or parent."
 *
 * WHAT THIS IS FOR. Measured 2026-08-31: the U.S. Dressage Festival of
 * Champions ran Aug 24–30 and crowned at least seven national champions across
 * separate divisions, plus para dressage grades. SR held ONE record for it,
 * with one date field and one champion field, and a second record for the same
 * event. Neither could ever be crowned — the same defect as a record named only
 * "PONY League World Series" when PONY runs seven of them.
 *
 * ⚠️ THE SPAN DECIDES THE SHAPE, NOT THE OPERATOR.
 *
 * If a person picks the structure they will pick inconsistently, and six months
 * later nobody can say why one festival is a folder and another is not. Derived
 * from the division dates it is the same answer every time and it can be
 * explained to whoever asks.
 *
 *   FOLDER    every division inside SAME_EVENT_DAYS → one event, many titles.
 *             The parent stays as the event, because the festival is the thing
 *             SR pitches against; you sell the Festival, not the Brentina Cup.
 *   SEPARATE  divisions months apart → these were never one event. Two
 *             championships that share a governing body. No parent, no
 *             children, each with its own date and its own key.
 *
 * ⚠️ THE THRESHOLD IS 10 DAYS, AND 21 WAS WRONG.
 *
 * 21 was proposed and rejected — correctly. The Dressage Festival is SEVEN
 * days. A state championship weekend is two or three. The Little League World
 * Series runs about ten. Nothing real sits between a fortnight and a season, so
 * the boundary only has to clear the longest single event and stay far below a
 * seasonal split. Ten does both. An Olympiad at sixteen days would need this
 * raised, and it is one constant.
 *
 * ⚠️ EVERY CHILD CARRIES ITS OWN DATE. This is the failure the current splitter
 * would create rather than fix: it copies the parent's `sr_event_date` onto
 * every sibling, so a seven-day festival becomes thirteen records all dated the
 * opening day. Every countdown, sweep window and pursuit stage then reads a
 * date that is wrong by up to a week — the exact defect `dateIntegrity()` was
 * built to catch, manufactured by our own tooling.
 *
 * ⚠️ EVERY CHILD CARRIES ITS OWN SUMMARY. A division record with a name and a
 * winner is not something a rep can act on. The summary fields — title wording,
 * runner-up, final score, venue, coach, season record — are what turns a row
 * into a ring proof or a conversation with a coach. They are per-division
 * because they ARE per-division: Junior Dressage and the Brentina Cup have
 * different winners, different scores and different stories.
 *
 * PURE. No fetch, no React, no clock beyond what is passed in. Every decision
 * here is testable, which is the point — this creates records in the system of
 * record and a wrong plan is expensive to undo.
 * ========================================================================= */

/** The longest a single championship event runs. See the header. */
export const SAME_EVENT_DAYS = 10

const DAY = 86400000
const S = (v) => (v == null ? '' : String(v)).trim()

/** Lowercase, punctuation-free, single-spaced — for keys and duplicate tests. */
export function slugify(v) {
  return S(v).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

function parseDay(v) {
  const s = S(v)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null
  const d = new Date(s + 'T00:00:00Z')
  return Number.isFinite(d.getTime()) && d.toISOString().slice(0, 10) === s ? d : null
}

/**
 * THE KEY IS THE EVENT, NOT THE RECORD SOMEBODY PRESSED.
 *
 * The existing splitter derives its idempotency key from the parent's key. SR
 * holds TWO records for the Dressage Festival, so splitting both produces two
 * families of siblings for the same divisions and nothing detects it.
 *
 * Keying on governing body + season + division means either parent yields the
 * same children, and the second attempt FINDS them instead of duplicating.
 * That makes the duplicate-parent problem structurally impossible rather than
 * something people have to remember.
 */
export function eventKey({ org, seasonYear, division }) {
  return ['champ', slugify(org), S(seasonYear), slugify(division)].filter(Boolean).join('|')
}

/** The story fields a rep needs to design a ring or walk into a meeting. */
export const STORY_FIELDS = [
  ['titleLine', 'title wording', 'the engraved line has to be invented without it'],
  ['runnerUp', 'runner-up', ''],
  ['score', 'final score', ''],
  ['venue', 'venue', ''],
  ['headCoach', 'head coach', 'the presentation line has no name without it'],
  ['seasonRecord', 'season record', ''],
  ['rosterUrl', 'championship-season roster', 'rings are personalised per player, so production cannot start without it'],
]

/**
 * What a rep can and cannot do with this division yet.
 *
 * Phrased as the CONSEQUENCE, not the absence — "no roster" means nothing to a
 * rep and "cannot personalise the ring" means something immediately. Same rule
 * the champion cell already follows.
 */
export function divisionReadiness(d) {
  const champion = S(d?.champion)
  const story = d?.story || {}
  if (!champion) {
    return { key: 'uncrowned', label: 'No winner recorded', blocks: ['Nothing to design or discuss yet.'] }
  }
  const missing = STORY_FIELDS.filter(([f]) => !S(story[f]))
  if (!missing.length) return { key: 'ready', label: 'READY', blocks: [] }
  return {
    key: 'needs',
    label: `NEEDS ${missing.length}`,
    blocks: missing.map(([, name, why]) => (why ? `no ${name} — ${why}` : `no ${name}`)),
  }
}

/**
 * planSplit({ parent, divisions })
 *
 * Returns the whole plan and writes nothing. Refusing to plan is a valid
 * outcome and comes back as `problems` — a split built on a bad plan creates
 * records in the system of record, and those are expensive to unpick.
 */
export function planSplit({ parent, divisions, sourceUrl } = {}) {
  const problems = []
  const list = Array.isArray(divisions) ? divisions : []

  if (!parent || !S(parent.id)) problems.push('no parent record to split')
  if (!/^https?:\/\//i.test(S(sourceUrl))) {
    problems.push('a split needs a source URL naming the divisions — creating records off an unsourced claim is how a fiction becomes data')
  }
  if (list.length < 2) problems.push('a split needs at least two divisions — one division is just a crown')

  /* Duplicate names are refused rather than merged. Two divisions with the same
   * name is somebody entering the same one twice, and silently collapsing them
   * would lose whichever champion was typed second. */
  const seen = new Set()
  list.forEach((d, i) => {
    const name = S(d?.division)
    if (!name) problems.push(`division ${i + 1} has no name`)
    else if (seen.has(slugify(name))) problems.push(`"${name}" appears twice`)
    seen.add(slugify(name))
    if (d?.champion != null && !S(d.champion)) {
      problems.push(`division ${i + 1} has an empty champion — leave it out rather than blank`)
    }
    if (S(d?.date) && !parseDay(d.date)) problems.push(`division ${i + 1} has an unreadable date "${S(d.date)}"`)
  })

  /* ---- THE SHAPE DECISION -------------------------------------------- */
  const parentDay = parseDay(parent?.eventRaw) || parseDay(parent?.dateRaw)
  const dated = list.map((d) => parseDay(d?.date)).filter(Boolean)
  /* A division with no stated date inherits the parent's — the source often
   * gives the event's window without breaking out which day each title landed,
   * and refusing the whole split over that would be worse than a date we
   * already have. It is recorded as inherited so nobody mistakes it for stated. */
  const spanDays = dated.length >= 2
    ? Math.round((Math.max(...dated.map((d) => d.getTime())) - Math.min(...dated.map((d) => d.getTime()))) / DAY)
    : 0

  const mode = spanDays > SAME_EVENT_DAYS ? 'separate' : 'folder'

  const children = list.map((d) => {
    const own = parseDay(d?.date)
    const day = own || parentDay
    return {
      division: S(d.division),
      champion: S(d.champion),
      gender: S(d.gender) || S(parent?.gender),
      date: day ? day.toISOString().slice(0, 10) : '',
      dateInherited: !own && Boolean(day),
      key: eventKey({ org: parent?.org || parent?.conference || parent?.name, seasonYear: parent?.seasonYear, division: d.division }),
      story: d.story || {},
      readiness: divisionReadiness(d),
      sourceUrl: S(sourceUrl),
    }
  })

  /* ---- WHAT HAPPENS TO THE RECORD SOMEBODY PRESSED -------------------- *
   *
   * Brian: "it becomes the new factual record."
   *
   * In BOTH modes it survives and is rewritten — never retired. Anything
   * already pointing at that id (a deal, a proof, a rep's link) keeps working,
   * which is the whole reason not to create thirteen fresh records and orphan
   * the original.
   *
   * FOLDER: it stays the event and becomes a container. It holds no champion,
   * and it must be excluded from the uncrowned worklist — a container that
   * counts as a gap gets hunted forever and can never be satisfied, which is
   * exactly the "this system does not work" impression we are trying to kill.
   *
   * SEPARATE: it becomes the EARLIEST division outright — renamed, redated, and
   * carrying that division's champion and story. It is no longer a container of
   * anything; the others are independent championships beside it. */
  const byDate = [...children].sort((a, b) => String(a.date).localeCompare(String(b.date)))
  const becomes = mode === 'separate' ? (byDate[0] || children[0]) : null

  const parentPlan = mode === 'folder'
    ? {
        role: 'container',
        name: S(parent?.name),
        holdsChampion: false,
        searchTag: 'split_into_divisions',
        excludeFromWorklist: true,
        note: `This event crowns ${children.length} champions across ${spanDays} day${spanDays === 1 ? '' : 's'}. The winners live on the division records.`,
      }
    : {
        role: 'becomes-division',
        name: becomes ? `${S(parent?.name)} — ${becomes.division}` : S(parent?.name),
        holdsChampion: true,
        becomes: becomes ? becomes.division : '',
        date: becomes ? becomes.date : '',
        excludeFromWorklist: false,
        note: `These titles are ${spanDays} days apart, so they were never one event. This record becomes the ${becomes ? becomes.division : 'first'} championship; the others stand beside it as their own records.`,
      }

  return {
    mode,
    spanDays,
    threshold: SAME_EVENT_DAYS,
    parent: parentPlan,
    children,
    /* In separate mode the record that was pressed IS one of the children, so
     * it must not also be created. Named explicitly rather than left for the
     * caller to work out from the mode. */
    toCreate: mode === 'separate' ? children.filter((c) => c !== becomes) : children,
    problems,
    ok: problems.length === 0,
  }
}
